// Per-observation & all/selected exports (CSV + branded landscape PDF) for the
// Observed Beacon History. Reuses the exact same scaffolding as the Vehicle /
// Person Log Book exports so all three stay visually consistent.
import { getFileTimestamp } from './timezone';
import {
  newLandscapeDoc,
  addBrandHeader,
  addFooters,
  loadLogoPng,
  downloadBlob,
  rowsToCsv,
  safeName,
  csvMapsLink,
  fmtCoords,
  tripLocMapsUrl,
  SLATE_900,
  SLATE_600,
  SLATE_500,
  BLUE,
  BORDER,
  formatExportDateTime,
} from './tripExport';

const stamp = () => getFileTimestamp();

const pointLoc = (p) => (p && p.latitude != null && p.longitude != null)
  ? { latitude: p.latitude, longitude: p.longitude }
  : null;

const seenNear = (p) => p.seen_near_label
  || (p.detected_by ? `Seen by ${p.detected_by.device_name || p.detected_by.device_imei || '-'}` : '-');

const battery = (p) => p.battery_voltage_mv != null
  ? `${p.battery_voltage_mv} mV${p.low_battery === true ? ' (Low)' : p.low_battery === false ? ' (OK)' : ''}`
  : 'Not reported';

// ----------------------------- CSV -----------------------------

const pointCsvRows = (p, idx) => ([
  [`Observation #${idx + 1}`, formatExportDateTime(p.timestamp)],
  ['Seen near', seenNear(p)],
  ['Confidence', p.confidence || 'low'],
  ['RSSI', p.rssi_dbm != null ? `${p.rssi_dbm} dBm` : '-'],
  ['Battery', battery(p)],
  ['Location source', p.location_source || 'detection only'],
  ['Detected by', p.detected_by?.device_name || p.detected_by?.device_imei || '-'],
  ['Location', csvMapsLink(pointLoc(p))],
]);

export const exportObsPointCsv = (point, personName, beaconName, idx = 0) => {
  const rows = [['Person', personName || ''], ['Beacon', beaconName || ''], []].concat(pointCsvRows(point, idx));
  downloadBlob(rowsToCsv(rows), `${safeName(personName)}-beacon-history-${idx + 1}-${stamp()}.csv`, 'text/csv;charset=utf-8;');
};

export const exportAllObsCsv = (points, personName, beaconName) => {
  let rows = [
    ['Person', personName || ''],
    ['Beacon', beaconName || ''],
    ['Observations', String((points || []).length)],
    ['Exported', formatExportDateTime(new Date().toISOString())],
    [],
  ];
  (points || []).forEach((p, i) => { rows = rows.concat(pointCsvRows(p, i), [[], []]); });
  downloadBlob(rowsToCsv(rows), `${safeName(personName)}-beacon-history_${stamp()}.csv`, 'text/csv;charset=utf-8;');
};

// ----------------------------- PDF -----------------------------

const drawPointBlock = (doc, layout, p, idx, startY) => {
  const { M, CONTENT_W, PAGE_H } = layout;
  let y = startY;

  doc.setFontSize(11); doc.setFont(undefined, 'bold'); doc.setTextColor(...SLATE_900);
  doc.text(`Observation ${idx + 1}   ${formatExportDateTime(p.timestamp)}`, M, y);
  doc.setFontSize(9); doc.setFont(undefined, 'bold'); doc.setTextColor(...SLATE_500);
  doc.text(String(p.confidence || 'low').toUpperCase(), layout.RIGHT, y, { align: 'right' });
  y += 16;

  const near = seenNear(p);
  if (near) {
    doc.setFontSize(9); doc.setFont(undefined, 'normal'); doc.setTextColor(...SLATE_600);
    doc.text(doc.splitTextToSize(near, CONTENT_W)[0] || near, M, y); y += 14;
  }

  const url = tripLocMapsUrl(pointLoc(p));
  if (url) {
    doc.setFontSize(8); doc.setFont(undefined, 'normal'); doc.setTextColor(...BLUE);
    const t = `Location: ${fmtCoords(pointLoc(p))}`;
    doc.textWithLink(t, M, y, { url }); y += 16;
  }

  const details = [
    ['RSSI', p.rssi_dbm != null ? `${p.rssi_dbm} dBm` : '-'],
    ['Battery', battery(p)],
    ['Source', p.location_source || 'detection only'],
    ['Detected by', p.detected_by?.device_name || p.detected_by?.device_imei || '-'],
  ];
  doc.setFontSize(8);
  details.forEach(([k, v]) => {
    if (y + 14 > PAGE_H - 44) { doc.addPage(); y = 44; }
    doc.setFont(undefined, 'bold'); doc.setTextColor(...SLATE_600); doc.text(`${k}:`, M, y);
    doc.setFont(undefined, 'normal'); doc.setTextColor(...SLATE_900);
    doc.text(doc.splitTextToSize(String(v), CONTENT_W - 90)[0] || String(v), M + 70, y);
    y += 13;
  });
  y += 6;
  return y;
};

export const exportObsPointPdf = async (point, personName, beaconName, idx = 0) => {
  const { doc, layout } = newLandscapeDoc();
  const logo = await loadLogoPng();
  let y = addBrandHeader(doc, layout, logo, 'Observed Beacon History', `${personName || '-'}${beaconName ? ' \u00b7 ' + beaconName : ''}`);
  y = drawPointBlock(doc, layout, point, idx, y);
  addFooters(doc, layout);
  doc.save(`${safeName(personName)}-beacon-history-${idx + 1}-${stamp()}.pdf`);
};

export const buildAllObsPdf = async (points, personName, beaconName) => {
  const { doc, layout } = newLandscapeDoc();
  const logo = await loadLogoPng();
  let y = addBrandHeader(doc, layout, logo, 'Observed Beacon History', `${personName || '-'}${beaconName ? ' \u00b7 ' + beaconName : ''} \u00b7 ${(points || []).length} observations`);
  const list = points || [];
  list.forEach((p, i) => {
    if (y > layout.PAGE_H - 110) { doc.addPage(); y = 44; }
    y = drawPointBlock(doc, layout, p, i, y);
    y += 10;
    if (i < list.length - 1) { doc.setDrawColor(...BORDER); doc.setLineWidth(0.5); doc.line(layout.M, y - 6, layout.RIGHT, y - 6); }
  });
  if (list.length === 0) { doc.setFontSize(10); doc.setTextColor(...SLATE_500); doc.text('No observations found for the selected filters.', layout.M, y); }
  addFooters(doc, layout);
  return { doc, filename: `${safeName(personName)}-beacon-history_${stamp()}.pdf` };
};

export const exportAllObsPdf = async (points, personName, beaconName) => {
  const { doc, filename } = await buildAllObsPdf(points, personName, beaconName);
  doc.save(filename);
};
